import {
  Navigate,
  Route,
  Routes,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { TenantSessionProvider } from './auth-context.js';
import { ActiveGroupProvider } from './active-group-context.js';
import { BrandingProvider } from './branding-context.js';
import { TemplateGallery } from './dev/TemplateGallery.js';
import { LoginPage } from './pages/LoginPage.js';
import { Shell } from './pages/Shell.js';
import { TemplatesPage } from './pages/TemplatesPage.js';
import { ContractsListPage } from './pages/ContractsListPage.js';
import { ContractEditorPage } from './pages/ContractEditorPage.js';
import { BetaContractEditorPage } from './pages/beta/BetaContractEditorPage.js';
import { AdaptTextPage } from './pages/beta/anpassa/AdaptTextPage.js';
import { BetaReviewPage } from './pages/beta/granska/BetaReviewPage.js';
import { ImportContractPage } from './pages/ImportContractPage.js';
import { BulkImportContractsPage } from './pages/BulkImportContractsPage.js';
import { OverviewPage } from './pages/OverviewPage.js';
import { ContractsInProgressPage } from './pages/ContractsInProgressPage.js';
import { HowItWorksPage } from './pages/HowItWorksPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { GroupsPage } from './pages/GroupsPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { PropertiesListPage } from './pages/PropertiesListPage.js';
import { PropertyDetailPage } from './pages/PropertyDetailPage.js';
import { ReviewsPage } from './pages/ReviewsPage.js';
import { SupportPage } from './pages/SupportPage.js';

function AppRoutes() {
  return (
    <Routes>
        {/* Dev-only template gallery — no auth. Build-time gated. */}
        {import.meta.env.DEV && (
          <Route path="/dev/templates" element={<TemplateGallery />} />
        )}

        {/* Authenticated app shell */}
        <Route
          element={
            <>
              <SignedIn>
                <TenantSessionProvider>
                  <BrandingProvider>
                    <ActiveGroupProvider>
                      <Shell />
                    </ActiveGroupProvider>
                  </BrandingProvider>
                </TenantSessionProvider>
              </SignedIn>
              <SignedOut>
                <LoginPage />
              </SignedOut>
            </>
          }
        >
          <Route path="/" element={<OverviewPage />} />
          <Route path="/oversikt/sa-fungerar-regta" element={<HowItWorksPage />} />
          <Route
            path="/oversikt/avtal-under-bearbetning"
            element={<ContractsInProgressPage />}
          />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/contracts" element={<ContractsListPage />} />
          <Route path="/contracts/import" element={<ImportContractPage />} />
          {/* Bulk import detail — share-able / refreshable result board
              for one batch. The entry point (the dropzone that creates
              a batch) lives on `/contracts/import`; the bare /bulk
              path redirects there. */}
          <Route
            path="/contracts/imports/bulk"
            element={<Navigate to="/contracts/import" replace />}
          />
          <Route path="/contracts/imports/bulk/:batchId" element={<BulkImportContractsPage />} />
          {/* Tracked single import — refreshable, share-able, browser-
              back works. Same component as /contracts/import; presence
              of :id flips it into tracking mode from first render. */}
          <Route path="/contracts/imports/:id" element={<ImportContractPage />} />
          <Route path="/contracts/:id/edit" element={<ContractEditorPage />} />
          {/* Beta lane — document-IR contracts (ADR-0099). Each editor
              redirects to the other when the contract belongs there. */}
          <Route path="/contracts/:id/beta" element={<BetaContractEditorPage />} />
          {/* "Anpassa text" (ADR-0102): adapt a beta contract's wording. */}
          <Route path="/contracts/:id/beta/anpassa" element={<AdaptTextPage />} />
          {/* Granskning (ADR-0102 addendum 2): field requests + text suggestions across all documents. */}
          <Route path="/contracts/:id/beta/granska" element={<BetaReviewPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/properties" element={<PropertiesListPage />} />
          <Route path="/properties/:id" element={<PropertyDetailPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
  );
}

// Single splat route hosting the existing descendant <Routes> tree. The
// only reason for the data router (vs. BrowserRouter) is that it enables
// useBlocker — see UnsavedChangesGuard.
const router = createBrowserRouter([{ path: '*', element: <AppRoutes /> }]);

export function App() {
  return <RouterProvider router={router} />;
}
